import { Link } from "react-router-dom";
import s from "./platformAdmin.module.css";
import {
  activityTone,
  clinicStatusLabel,
  daysLeftLabel,
  formatDateRu,
  formatDateTimeRu,
  formatRelativeActivity,
  formatRub,
  getOutreachReasons,
  OUTREACH_REASON_LABELS,
  statusClass,
} from "./platformClubsUtils";

function OwnerContact({ owner }) {
  if (!owner) {
    return <span className={s.clubMetaMuted}>Владелец не найден</span>;
  }
  return (
    <div className={s.clubOwner}>
      <strong>{owner.full_name || "Владелец"}</strong>
      <div className={s.clubOwnerLinks}>
        {owner.email ? (
          <a href={`mailto:${owner.email}`} className={s.clubContactLink}>
            {owner.email}
          </a>
        ) : null}
        {owner.phone ? (
          <a href={`tel:${owner.phone}`} className={s.clubContactLink}>
            {owner.phone}
          </a>
        ) : null}
      </div>
    </div>
  );
}

function ActivityBadge({ clinic }) {
  const at = clinic.activity?.last_activity_at;
  const tone = activityTone(at);
  return (
    <div className={s.clubActivity}>
      <span className={`${s.activityDot} ${s[`activityDot${tone}`]}`} aria-hidden />
      <div>
        <strong>{formatRelativeActivity(at)}</strong>
        <small>
          {clinic.activity?.last_staff_login_at
            ? `Вход: ${formatDateTimeRu(clinic.activity.last_staff_login_at)}`
            : "Входов не было"}
        </small>
      </div>
    </div>
  );
}

function StatusPill({ clinic }) {
  const cls = statusClass(clinic.status, clinic.billing);
  return (
    <span className={`${s.statusPill} ${s[cls]}`}>
      {clinicStatusLabel(clinic)}
    </span>
  );
}

function ClubStat({ label, value }) {
  return (
    <div className={s.clubCardStat}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ClubCard({
  clinic,
  showStatusSelect,
  showDeleteClinicActions,
  onStatusChange,
  onDeleteClinic,
  showOutreachReasons,
  deletingId,
}) {
  const plan =
    clinic.billing?.plan_label || clinic.plan?.name || clinic.plan_tier || "Тариф —";
  const tone = activityTone(clinic.activity?.last_activity_at);
  const days = clinic.billing?.days_left;
  const daysUrgent = days != null && days <= 14;
  const reasons = showOutreachReasons ? getOutreachReasons(clinic) : [];

  return (
    <article className={`${s.clubCard} ${s[`clubCardTone${tone}`] || ""}`}>
      <div className={s.clubCardTop}>
        <div className={s.clubCardIdentity}>
          <Link to={`/admin/tenants/${clinic.id}`} className={s.clubCardName}>
            {clinic.name}
          </Link>
          <span className={s.clubCardSub}>{clinic.subdomain}</span>
        </div>
        <StatusPill clinic={clinic} />
      </div>

      {reasons.length ? (
        <div className={s.clubCardReasons}>
          {reasons.map((id) => (
            <span key={id} className={s.clubCardReason}>
              {OUTREACH_REASON_LABELS[id] || id}
            </span>
          ))}
        </div>
      ) : null}

      <div className={s.clubCardPlanRow}>
        <span className={s.clubCardPlan}>{plan}</span>
        <span className={`${s.clubCardDays} ${daysUrgent ? s.clubCardDaysWarn : ""}`}>
          {daysLeftLabel(days)}
          {clinic.billing?.paid_until
            ? ` · до ${formatDateRu(clinic.billing.paid_until)}`
            : ""}
        </span>
      </div>

      <div className={s.clubCardStats}>
        <ClubStat label="Клиенты" value={clinic.stats?.clients ?? 0} />
        <ClubStat label="Тренеры" value={clinic.stats?.trainers ?? 0} />
        <ClubStat
          label="Занятия 30д"
          value={clinic.stats?.sessions_30d ?? 0}
        />
        <ClubStat
          label="Выручка 30д"
          value={`${formatRub(clinic.stats?.revenue_30d)} ₽`}
        />
      </div>

      <div className={s.clubCardFooter}>
        <ActivityBadge clinic={clinic} />
        <OwnerContact owner={clinic.owner} />
      </div>

      {(showStatusSelect || showDeleteClinicActions) && (
        <div className={s.clubCardActions}>
          {showStatusSelect ? (
            <select
              className={s.clubCardSelect}
              value={clinic.status}
              onChange={(e) => onStatusChange?.(clinic.id, e.target.value)}
              aria-label="Статус клуба"
            >
              <option value="trial">trial</option>
              <option value="active">active</option>
              <option value="suspended">suspended</option>
            </select>
          ) : null}
          <Link to={`/admin/tenants/${clinic.id}`} className={s.clubCardOpen}>
            Открыть
          </Link>
          {showDeleteClinicActions ? (
            <button
              type="button"
              className={`${s.btn} ${s.btnDanger}`}
              onClick={() => onDeleteClinic?.(clinic.id)}
              disabled={!onDeleteClinic || Boolean(deletingId)}
            >
              {deletingId === clinic.id ? "Удаление…" : "Удалить"}
            </button>
          ) : null}
        </div>
      )}
    </article>
  );
}

export default function PlatformClubsList({
  clinics,
  onStatusChange,
  showStatusSelect = false,
  showDeleteClinicActions = false,
  onDeleteClinic,
  deletingId = null,
  emptyText = "Клубов пока нет",
  view = "cards",
  showOutreachReasons = false,
}) {
  if (!clinics.length) {
    return <p className={s.emptyHint}>{emptyText}</p>;
  }

  return (
    <>
      <div className={s.clubCardsGrid}>
        {clinics.map((c) => (
          <ClubCard
            key={c.id}
            clinic={c}
            showStatusSelect={showStatusSelect}
            showDeleteClinicActions={showDeleteClinicActions}
            onStatusChange={onStatusChange}
            onDeleteClinic={onDeleteClinic}
            showOutreachReasons={showOutreachReasons}
            deletingId={deletingId}
          />
        ))}
      </div>

      {view === "table" ? (
        <div className={s.clubTableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Клуб</th>
                <th>Владелец</th>
                <th>Активность</th>
                <th>Тариф</th>
                <th>Оплата</th>
                <th>Клиенты</th>
                <th>Выручка 30д</th>
                {showStatusSelect ? <th>Статус</th> : null}
                {showDeleteClinicActions ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {clinics.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/admin/tenants/${c.id}`} className={s.subClubLink}>
                      <strong>{c.name}</strong>
                      <small>{c.subdomain}</small>
                    </Link>
                  </td>
                  <td>
                    <OwnerContact owner={c.owner} />
                  </td>
                  <td>
                    <ActivityBadge clinic={c} />
                  </td>
                  <td>
                    {c.billing?.plan_label || c.plan?.name || c.plan_tier || "—"}
                  </td>
                  <td>
                    {formatDateRu(c.billing?.paid_until)}
                    <br />
                    <small>{daysLeftLabel(c.billing?.days_left)}</small>
                  </td>
                  <td>{c.stats?.clients ?? 0}</td>
                  <td>{formatRub(c.stats?.revenue_30d)} ₽</td>
                  {showStatusSelect ? (
                    <td>
                      <select
                        value={c.status}
                        onChange={(e) => onStatusChange?.(c.id, e.target.value)}
                      >
                        <option value="trial">trial</option>
                        <option value="active">active</option>
                        <option value="suspended">suspended</option>
                      </select>
                    </td>
                  ) : null}
                  {showDeleteClinicActions ? (
                    <td style={{ width: 140, textAlign: "right" }}>
                      <button
                        type="button"
                        className={`${s.btn} ${s.btnDanger}`}
                        onClick={() => onDeleteClinic?.(c.id)}
                        disabled={!onDeleteClinic || Boolean(deletingId)}
                      >
                        {deletingId === c.id ? "Удаление…" : "Удалить"}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
